import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BlockStack, Box, Button, Card, Layout, Page, Modal, useSetIndexFiltersMode } from '@shopify/polaris';
import { useBlocker, useLocation, useNavigate, useParams } from 'react-router';
import axios from 'axios';

// Components
import { useToast } from '#client/components/common/Toast/ToastContext';
import PurchaseOrderActionButtons from './components/PurchaseOrderActionButtons';
import PurchaseOrderEntrySection from './components/PurchaseOrderEntrySection';
import PurchaseOrderLineItemsTable from './components/PurchaseOrderLineItemsTable/PurchaseOrderLineItemsTable';
import PurchaseOrderReceivingHeader from './components/PurchaseOrderReceivingHeader';
import ActivityLog from '#client/components/ActivityLog';

// Custom hooks and utilities
import { useAsyncButton, useBarcodeScanner, useReceivingDocumentActions, useReceivingDocumentLoading } from './utils/hooks';
import { fetchCartonLinesByLicensePlate, handleReceivingCarton, handleReceivingLineItems, fetchProductFromServer } from './utils/helpers';
import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';
import PurchaseOrderBreadcrumbs from '../PurchaseOrderBreadcrumbs';

/**
 * Manages navigation modal for unsaved changes
 */
const useNavigationModal = (isDirty, setPendingAction) => {
    const [showNavigationModal, setShowNavigationModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);
    const [hasTriedToNavigateRows, setHasTriedToNavigateRows] = useState(new Set()); // Track which rows have tried to navigate
    const navigate = useNavigate();

    const handleNavigationCancel = useCallback((editingKey = null) => {
        setShowNavigationModal(false);
        setPendingNavigation(null);

        if (editingKey !== null) {
            setHasTriedToNavigateRows((prev) => new Set([...prev, editingKey]));
        }
    }, []);

    // Use a ref to track if we're in the middle of a confirmed navigation
    const isNavigatingRef = useRef(false);

    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        // Don't block if we're in the middle of a confirmed navigation
        if (isNavigatingRef.current) {
            isNavigatingRef.current = false;
            return false;
        }

        if (!isDirty) return false;

        if (currentLocation.pathname !== nextLocation.pathname) {
            if (typeof setPendingAction === 'function') setPendingAction('navigation');
            setPendingNavigation(nextLocation);
            setShowNavigationModal(true);
            return true; // Block the navigation
        }
        return false;
    }, isDirty);

    // Modified confirm handler that sets the flag
    const handleNavigationConfirm = useCallback(() => {
        if (pendingNavigation) {
            setShowNavigationModal(false);
            isNavigatingRef.current = true;

            const path = pendingNavigation.pathname + (pendingNavigation.search || '') + (pendingNavigation.hash || '');

            navigate(path, { replace: true });
            setPendingNavigation(null);
        }
    }, [pendingNavigation, navigate]);

    // Handle browser reload/close events
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isDirty) {
                event.preventDefault();
                event.returnValue = '';
                return '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isDirty]);

    // Function to reset the tried to navigate flag for a specific row
    const resetTriedToNavigateForRow = useCallback((rowKey) => {
        setHasTriedToNavigateRows((prev) => {
            const newSet = new Set(prev);
            newSet.delete(rowKey);
            return newSet;
        });
    }, []);

    // Function to mark a row as having tried to navigate (for reload attempts)
    const markRowTriedToNavigate = useCallback((rowKey) => {
        setHasTriedToNavigateRows((prev) => new Set([...prev, rowKey]));
    }, []);

    // Function to clear all navigation flags (for page reload)
    const clearAllNavigationFlags = useCallback(() => {
        setHasTriedToNavigateRows(new Set());
    }, []);
    return {
        showNavigationModal,
        setShowNavigationModal,
        handleNavigationConfirm,
        handleNavigationCancel,
        blocker,
        isNavigatingRef, // Expose the ref so parent can access it
        hasTriedToNavigateRows,
        resetTriedToNavigateForRow,
        markRowTriedToNavigate,
        clearAllNavigationFlags,
    };
};

/**
 * Manages the receiving mode state and transitions
 */
const useReceivingMode = () => {
    const [receivingLineItemsActive, setReceivingLineItemsActive] = useState(false);
    const [receivingCartonsActive, setReceivingCartonsActive] = useState(false);
    const [entryMode, setEntryMode] = useState(PURCHASE_ORDER_STRINGS.INITIAL_LOAD);
    const [receivingMode, setReceivingMode] = useState(null);

    const setReceivingModeHandler = useCallback((mode) => {
        if (mode === PURCHASE_ORDER_STRINGS.SCAN_MODE) {
            setEntryMode(PURCHASE_ORDER_STRINGS.SCAN_MODE);
        } else {
            setEntryMode(PURCHASE_ORDER_STRINGS.MANUAL_ENTRY);
        }

        setReceivingMode(mode === PURCHASE_ORDER_STRINGS.SCAN_MODE ? 'receiving-line-items' : null);
    }, []);

    const setReceivingCartonModeHandler = useCallback((mode) => {
        if (mode === PURCHASE_ORDER_STRINGS.SCAN_MODE) {
            setEntryMode(PURCHASE_ORDER_STRINGS.SCAN_MODE);
        } else {
            setEntryMode(PURCHASE_ORDER_STRINGS.MANUAL_ENTRY);
        }

        setReceivingMode(mode === PURCHASE_ORDER_STRINGS.SCAN_MODE ? 'receiving-cartons' : null);
    }, []);

    return {
        receivingLineItemsActive,
        setReceivingLineItemsActive,
        receivingCartonsActive,
        setReceivingCartonsActive,
        entryMode,
        setEntryMode,
        receivingMode,
        setReceivingModeHandler,
        setReceivingCartonModeHandler,
    };
};

/**
 * Provides dirty state detection to track unsaved changes
 */
const useVariantsState = () => {
    const [scannedVariants, setScannedVariants] = useState([]);
    const [baselineVariants, setBaselineVariants] = useState([]);
    const [baselineSet, setBaselineSet] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [, setItemEntryMethods] = useState({});
    const [editingItems, setEditingItems] = useState(new Set());
    // Track when user has clicked edit or made edits but hasn't saved the document
    const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);

    /**
     * Deep comparison function to check if two objects have any differences
     * Defined inside the hook for encapsulation but as a regular function to avoid recreation on each render
     */
    const isStateDirty = (originalObject, updatedObject) => {
        // Quick reference equality check
        if (originalObject === updatedObject) return false;

        // Handle null/undefined cases and non-object types (primitives)
        if (originalObject == null || updatedObject == null || typeof originalObject !== 'object' || typeof updatedObject !== 'object') {
            return true;
        }

        const keys1 = Object.keys(originalObject);
        const keys2 = Object.keys(updatedObject);

        // Different number of keys means objects are different
        if (keys1.length !== keys2.length) return true;

        // Convert keys2 to Set
        const keys2Set = new Set(keys2);

        // Check each key in the original object
        for (const key of keys1) {
            // Key missing in updated object then set to true
            if (!keys2Set.has(key)) return true;

            // Recursively check nested objects/arrays
            if (isStateDirty(originalObject[key], updatedObject[key])) return true;
        }

        return false;
    };

    /**
     * Create a deep clone of baseline for stable comparison
     * This prevents issues with object mutations affecting dirty detection
     * Performance: ~1ms per 1000 items, ~10ms at 10k items
     */
    const clonedBaseline = useMemo(() => {
        if (!baselineSet || !baselineVariants) return null;
        return structuredClone(baselineVariants);
    }, [baselineSet, baselineVariants]);

    /**
     * Determine if the form has unsaved changes by comparing
     * current state with the baseline
     */
    const isDirty = useMemo(() => {
        // Check if user has unsaved edits (made qty changes without saving document)
        if (editingItems.size > 0 || hasUnsavedEdits) return true;

        // No changes if baseline not set or user hasn't interacted, or no baseline to compare against
        if (!baselineSet || !hasInteracted || !clonedBaseline) return false;

        // Deep comparison between current and baseline state
        return isStateDirty(clonedBaseline, scannedVariants);
    }, [baselineSet, hasInteracted, scannedVariants, clonedBaseline, hasUnsavedEdits, editingItems]);

    /**
     * Reset the baseline to current state (e.g., after saving)
     * This marks the current state as "clean"
     */
    const resetBaseline = useCallback(() => {
        // Create new array reference to ensure state update
        setBaselineVariants([...scannedVariants]);
        setBaselineSet(true);
        setHasInteracted(false);
        setHasUnsavedEdits(false);
    }, [scannedVariants]);

    /**
     * Initialize both working and baseline variants
     * Used when first loading data
     */
    const initializeVariants = useCallback((variants) => {
        setScannedVariants(variants);
        setBaselineVariants(variants);
        setBaselineSet(true);
    }, []);

    const setItemEditing = useCallback((itemId, isEditing = true) => {
        setEditingItems((prev) => {
            const newSet = new Set(prev);

            if (isEditing) {
                newSet.add(itemId);
            } else {
                newSet.delete(itemId);
            }
            return newSet;
        });
    }, []);

    const clearAllEditing = useCallback(() => {
        setEditingItems(new Set());
    }, []);

    const setUnsavedEdits = useCallback((hasEdits) => {
        setHasUnsavedEdits(hasEdits);
    }, []);

    return {
        scannedVariants,
        setScannedVariants,
        baselineVariants,
        isDirty,
        hasInteracted,
        setHasInteracted,
        resetBaseline,
        initializeVariants,
        setItemEntryMethods,
        editingItems,
        setItemEditing,
        clearAllEditing,
        hasUnsavedEdits,
        setUnsavedEdits,
    };
};

/**
 * Manages purchase order data and related computed values
 */
const usePurchaseOrderData = (poId, rdId) => {
    const [purchaseOrder, setPurchaseOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch purchase order data
    const fetchPurchaseOrder = useCallback(async () => {
        try {
            const API_URL = `${import.meta.env.VITE_API_URL}/api/app/purchase-orders/${poId}`;
            const response = await axios.get(API_URL);
            setPurchaseOrder(response.data.data.purchaseOrder);
        } catch (error) {
            throw new Error('Failed to fetch purchase order details.');
        }
    }, [poId]);

    // Get current receiving document
    const currentReceivingDocument = useMemo(() => purchaseOrder?.receivingDocuments?.find((doc) => String(doc.id) === String(rdId)), [purchaseOrder, rdId]);

    // Get saved variants from current document
    const getSavedVariants = useCallback(() => {
        return currentReceivingDocument?.scannedVariants || [];
    }, [currentReceivingDocument]);

    // Computed business rules
    const isAuditRequired = useMemo(() => purchaseOrder?.advancedShippingNotices?.some((asn) => asn.isAuditTrigger), [purchaseOrder]);
    const hasASN = useMemo(() => purchaseOrder?.advancedShippingNotices && purchaseOrder.advancedShippingNotices.length > 0, [purchaseOrder]);

    return {
        purchaseOrder,
        setPurchaseOrder,
        loading,
        setLoading,
        fetchPurchaseOrder,
        currentReceivingDocument,
        getSavedVariants,
        isAuditRequired,
        hasASN,
    };
};

/**
 * Manages document status with local override capability
 */
const useDocumentStatus = (currentStatus) => {
    const [status, setStatus] = useState();
    const [hasLocalStatusUpdate, setHasLocalStatusUpdate] = useState(false);

    const displayStatus = useMemo(() => {
        return hasLocalStatusUpdate && status ? status : currentStatus;
    }, [hasLocalStatusUpdate, status, currentStatus]);

    const resetStatusUpdate = useCallback(() => {
        setHasLocalStatusUpdate(false);
    }, []);

    return {
        status,
        setStatus,
        hasLocalStatusUpdate,
        setHasLocalStatusUpdate,
        displayStatus,
        resetStatusUpdate,
    };
};

// ========== MAIN COMPONENT ==========

const PurchaseOrderReceivingDocument = () => {
    const [pendingAction, setPendingAction] = useState(null);
    const cancelEditRef = useRef(null);
    const currentEditingIndexRef = useRef(null);

    // Router hooks
    const { mode, setMode } = useSetIndexFiltersMode();
    const location = useLocation();
    const navigate = useNavigate();
    const { poId, rdId, asnId } = useParams();
    const passedState = location?.state;
    const { addToast } = useToast();

    // ========== CONSOLIDATED HOOKS ==========
    const receivingMode = useReceivingMode();
    const variants = useVariantsState();
    const purchaseOrderData = usePurchaseOrderData(poId, rdId);
    const documentStatus = useDocumentStatus(purchaseOrderData.currentReceivingDocument?.status);
    const navigationModal = useNavigationModal(variants.isDirty || variants.editingItems.size > 0, setPendingAction);

    // ========== REMAINING SIMPLE STATE ==========
    const [qtyValue, setQtyValue] = useState('1');
    const [, setValue] = useState('');
    const [submitClicked, setSubmitClicked] = useState(false);
    const [, setLastScanned] = useState(null);
    const [lastAdded, setLastAdded] = useState(null);

    // ========== COMPUTED VALUES ==========
    const hasOverreceived = useMemo(() => variants.scannedVariants.some((entry) => entry.quantityReceived > entry.quantityExpected), [variants.scannedVariants]);

    const productOnPurchaseOrder = useMemo(() => variants.scannedVariants.some((entry) => entry.productOnPurchaseOrder), [variants.scannedVariants]);

    const totalItems = useMemo(() => variants.scannedVariants.reduce((total, entry) => total + Number(entry.qtyReceived ?? 0), 0), [variants.scannedVariants]);

    const pageTitle = useMemo(() => {
        const strings = PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT;

        if (receivingMode.entryMode === PURCHASE_ORDER_STRINGS.INITIAL_LOAD) {
            return strings.RECEIVING_DOC_VIEW;
        }

        if (passedState?.status === PURCHASE_ORDER_STRINGS.DRAFT) {
            return receivingMode.entryMode === PURCHASE_ORDER_STRINGS.SCAN_MODE ? strings.RECEIVING_DOC_SCAN_MODE_VIEW : strings.RECEIVING_DOC_MANUAL_ENTRY_MODE_VIEW;
        }

        return strings.RECEIVING_DOC_VIEW;
    }, [receivingMode.entryMode, passedState?.status]);

    // ========== RECEIVING BUTTONS WITH ASYNC HANDLING ==========
    const [receivingLineItemsLoading, receivingLineItemButtonClick] = useAsyncButton(() =>
        handleReceivingLineItems(receivingMode.setReceivingLineItemsActive, receivingMode.setReceivingCartonsActive, receivingMode.setReceivingModeHandler)
    );

    const [receivingCartonsLoading, receivingCartonButtonClick] = useAsyncButton(() =>
        handleReceivingCarton(receivingMode.setReceivingCartonsActive, receivingMode.setReceivingLineItemsActive, receivingMode.setReceivingCartonModeHandler)
    );

    const handleRdIdUpdate = useCallback(
        (newRdId, wasNewDocument) => {
            if (wasNewDocument && newRdId && rdId === 'new') {
                // Update the URL to use the new database ID
                const currentPath = location.pathname;
                const newPath = currentPath.replace('/new', `/${newRdId}`);

                // Set the flag to bypass the blocker for this navigation
                navigationModal.isNavigatingRef.current = true;

                // Replace the current history entry so refresh works with new ID
                navigate(newPath, {
                    replace: true,
                    state: location.state, // Preserve any existing state
                });

                // Reset the baseline since we just saved successfully
                // This prevents the modal from showing after the URL update
                variants.resetBaseline();
            }
        },
        [rdId, location.pathname, location.state, navigate, navigationModal.isNavigatingRef, variants.resetBaseline]
    );

    // ========== CUSTOM HOOKS ==========
    useReceivingDocumentLoading({
        scannedVariants: variants.scannedVariants,
        setLoading: purchaseOrderData.setLoading,
    });

    const [currentName, setCurrentName] = useState('');

    // Add callback to handle name updates
    const handleNameUpdate = useCallback((newName) => {
        setCurrentName(newName);
    }, []);

    const documentActions = useReceivingDocumentActions({
        poId,
        rdId,
        asnId,
        scannedVariants: variants.scannedVariants,
        setStatus: documentStatus.setStatus,
        setHasLocalStatusUpdate: documentStatus.setHasLocalStatusUpdate,
        onRdIdUpdate: handleRdIdUpdate,
        onNameUpdate: handleNameUpdate,
        entryMode: receivingMode.entryMode,
    });

    const name = currentName || purchaseOrderData.currentReceivingDocument?.name;

    useBarcodeScanner({
        enabled: receivingMode.entryMode === PURCHASE_ORDER_STRINGS.SCAN_MODE && documentStatus.displayStatus !== PURCHASE_ORDER_STRINGS.COMPLETED,
        poId,
        rdId,
        receivingCartonsActive: receivingMode.receivingCartonsActive,
        receivingLineItemsActive: receivingMode.receivingLineItemsActive,
        entryMode: receivingMode.entryMode,
        qtyValue,
        fetchCartonLinesByLicensePlate,
        fetchProductFromServer,
        setScannedVariants: variants.setScannedVariants,
        setLoading: purchaseOrderData.setLoading,
        setValue,
        setLastScanned,
        purchaseOrder: purchaseOrderData.purchaseOrder,
        setLastAdded,
        setHasInteracted: variants.setHasInteracted,
    });

    // ========== EVENT HANDLERS ==========
    const handleRemove = useCallback(
        (indexToRemove) => {
            variants.setScannedVariants((prev) => {
                const copy = [...prev];
                copy.splice(indexToRemove, 1);
                return copy;
            });
            // Mark that user has interacted with the form
            variants.setHasInteracted(true);
        },
        [variants.setScannedVariants, variants.setHasInteracted]
    );

    const getResourceId = useCallback((entry) => {
        return [entry.variant?.id, entry.barcode].filter(Boolean).join('-');
    }, []);

    const handleSaveDraftAndDismissBar = useCallback(async () => {
        // If any row is in edit mode, show the navigation modal and block save
        if (variants.editingItems.size > 0) {
            // Mark all editing items by their keys
            variants.editingItems.forEach((editingKey) => {
                navigationModal.markRowTriedToNavigate(editingKey);
            });
            setPendingAction('save draft');
            navigationModal.setShowNavigationModal(true);
            return;
        }

        // Cancel any active editing first
        if (cancelEditRef.current) {
            cancelEditRef.current();
        }

        variants.clearAllEditing();

        await documentActions.handleSaveDraft();
        variants.resetBaseline();
    }, [documentActions.handleSaveDraft, variants.resetBaseline, variants.clearAllEditing, variants.editingItems, navigationModal]);

    // Handler for confirming Save Draft from modal
    const handleConfirmSaveDraft = useCallback(async () => {
        if (cancelEditRef.current) {
            cancelEditRef.current();
        } else {
            await variants.commitAllEditing();
        }
        // Now save draft
        await documentActions.handleSaveDraft();
        variants.clearAllEditing();
        variants.resetBaseline();
        receivingMode.setReceivingLineItemsActive(false);
        receivingMode.setReceivingCartonsActive(false);
        receivingMode.setEntryMode(PURCHASE_ORDER_STRINGS.INITIAL_LOAD);

        if (receivingMode.setReceivingModeHandler) {
            receivingMode.setReceivingModeHandler(null);
        }
    }, [documentActions.handleSaveDraft, variants.resetBaseline, variants.clearAllEditing, variants.commitAllEditing, cancelEditRef, receivingMode]);

    const handleSubmitAndDismissBar = useCallback(async () => {
        if (variants.editingItems.size > 0) {
            // Mark all editing rows as tried to navigate so the red border appears in the modal
            variants.editingItems.forEach((editingIndex) => {
                navigationModal.markRowTriedToNavigate(editingIndex);
            });
            setPendingAction('submit document');
            navigationModal.setShowNavigationModal(true);
            return;
        }
        variants.clearAllEditing();
        await documentActions.handleSubmitDocument();
        variants.resetBaseline();
    }, [documentActions.handleSubmitDocument, variants.resetBaseline, variants.clearAllEditing, variants.editingItems, navigationModal]);

    const onReceivingLineItemsClick = useCallback(async () => {
        await handleReceivingLineItems(receivingMode.setReceivingLineItemsActive, receivingMode.setReceivingCartonsActive, receivingMode.setReceivingModeHandler);
    }, [receivingMode.setReceivingLineItemsActive, receivingMode.setReceivingCartonsActive, receivingMode.setReceivingModeHandler]);

    const onReceivingCartonClick = useCallback(async () => {
        await handleReceivingCarton(receivingMode.setReceivingCartonsActive, receivingMode.setReceivingLineItemsActive, receivingMode.setReceivingCartonModeHandler);
    }, [receivingMode.setReceivingCartonsActive, receivingMode.setReceivingLineItemsActive, receivingMode.setReceivingCartonModeHandler]);

    // ========== EFFECTS ==========
    useEffect(() => {
        purchaseOrderData.fetchPurchaseOrder().catch(addToast);
        documentStatus.resetStatusUpdate();
    }, [purchaseOrderData.fetchPurchaseOrder, documentStatus.resetStatusUpdate]);

    useEffect(() => {
        if (purchaseOrderData.purchaseOrder) {
            const savedVariants = purchaseOrderData.getSavedVariants();
            variants.initializeVariants(savedVariants);
        }
    }, [purchaseOrderData.purchaseOrder, purchaseOrderData.getSavedVariants, variants.initializeVariants]);

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (!variants.isDirty) return;
            event.preventDefault();
            event.returnValue = '';
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [variants.isDirty]);

    const shouldShowEntrySection = (receivingMode.receivingCartonsActive || receivingMode.receivingLineItemsActive) && !submitClicked;
    const shouldShowActionButtons = passedState?.status !== PURCHASE_ORDER_STRINGS.COMPLETED || passedState?.parentDataStatus !== PURCHASE_ORDER_STRINGS.COMPLETED;
    const shouldShowReceivingButtons = documentStatus.displayStatus !== PURCHASE_ORDER_STRINGS.COMPLETED;

    const hasAnyZeroReceived = (variants) => {
        for (let i = 0; i < variants.scannedVariants.length; i++) {
            if (Number(variants.scannedVariants[i].qtyReceived) === 0) {
                return true;
            }
        }

        return false;
    };

    const anyZeroReceived = hasAnyZeroReceived(variants);

    // Modal action creators
    const createGoBackAction = useCallback(
        () => ({
            content: PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.UNSAVED_CHANGES_MODAL.GO_BACK,
            onAction: () => {
                if (currentEditingIndexRef.current !== null) {
                    navigationModal.markRowTriedToNavigate(currentEditingIndexRef.current);
                }

                setSubmitClicked(false);
                setPendingAction(null);

                navigationModal.handleNavigationCancel();
            },
            destructive: false,
        }),
        [navigationModal]
    );

    const createContinueAction = useCallback(
        (actionHandler) => ({
            content: PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.UNSAVED_CHANGES_MODAL.CONTINUE_WITHOUT_SAVING,
            onAction: async () => {
                await actionHandler();
                setPendingAction(null);
                navigationModal.setShowNavigationModal(false);
            },
            destructive: true,
        }),
        [navigationModal]
    );

    const createStayOnPageAction = useCallback(
        () => ({
            content: PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.UNSAVED_CHANGES_MODAL.STAY_ON_PAGE,
            onAction: () => {
                if (currentEditingIndexRef.current !== null) {
                    navigationModal.markRowTriedToNavigate(currentEditingIndexRef.current);
                }
                setSubmitClicked(false);
                setPendingAction(null);
                navigationModal.handleNavigationCancel();
            },
            destructive: false,
        }),
        [navigationModal]
    );

    // Modal configurations lookup
    const modalConfigs = useMemo(
        () => ({
            'save draft': {
                text: PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.UNSAVED_CHANGES_MODAL.SAVE_DRAFT,
                primaryAction: createContinueAction(handleConfirmSaveDraft),
                secondaryActions: [createGoBackAction()],
            },
            'submit document': {
                text: PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.UNSAVED_CHANGES_MODAL.SUBMIT_DOCUMENT,
                primaryAction: createContinueAction(async () => {
                    if (cancelEditRef.current) {
                        cancelEditRef.current();
                    } else {
                        await variants.commitAllEditing();
                    }

                    await documentActions.handleSubmitDocument();
                    variants.clearAllEditing();
                    variants.resetBaseline();
                    receivingMode.setReceivingLineItemsActive(false);
                    receivingMode.setReceivingCartonsActive(false);
                    receivingMode.setEntryMode(PURCHASE_ORDER_STRINGS.INITIAL_LOAD);

                    if (receivingMode.setReceivingModeHandler) {
                        receivingMode.setReceivingModeHandler(null);
                    }
                }),
                secondaryActions: [createGoBackAction()],
            },
            default: {
                text: PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.UNSAVED_CHANGES_MODAL.NAVIGATION,
                primaryAction: {
                    content: PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.UNSAVED_CHANGES_MODAL.LEAVE_WITHOUT_SAVING,
                    onAction: navigationModal.handleNavigationConfirm,
                    destructive: true,
                },
                secondaryActions: [createStayOnPageAction()],
            },
        }),
        [createGoBackAction, createContinueAction, createStayOnPageAction, handleConfirmSaveDraft, variants, documentActions, cancelEditRef, navigationModal]
    );

    const modalConfig = {
        open: navigationModal.showNavigationModal,
        onClose: navigationModal.handleNavigationCancel,
        title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.UNSAVED_CHANGES_MODAL.TITLE,
        ...(modalConfigs[pendingAction] || modalConfigs.default),
    };

    return (
        <>
            <Modal {...modalConfig}>
                <Modal.Section>
                    <BlockStack>
                        <p>{modalConfig.text}</p>
                    </BlockStack>
                </Modal.Section>
            </Modal>
            <Box paddingBlockEnd="400" className="purchase-order-receiving-document-page">
                <PurchaseOrderBreadcrumbs
                    nsId={purchaseOrderData?.purchaseOrder?.netSuiteHumanPoNumber}
                    poId={poId}
                    asnId={asnId}
                    rdId={rdId}
                    name={name}
                    navigateFromASN={passedState?.navigateFromASN ? passedState.navigateFromASN : false}
                    asnIdValue={purchaseOrderData?.purchaseOrder?.advancedShippingNotices?.[0]?.netsuiteId}
                />

                <Page fullWidth className="purchase-order-receiving-document-page">
                    {/* Page Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ flex: 1, fontSize: '20px', fontWeight: 600, height: 'auto' }}>{pageTitle}</span>
                        {/* Receiving Mode Buttons */}
                        {shouldShowReceivingButtons && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 24 }}>
                                <Button
                                    variant="primary"
                                    tone={receivingMode.receivingLineItemsActive ? 'critical' : 'plain'}
                                    onClick={receivingLineItemButtonClick}
                                    loading={receivingLineItemsLoading}
                                >
                                    {receivingMode.receivingLineItemsActive
                                        ? PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.STOP_RECEIVING_LINE_ITEMS
                                        : PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.RECEIVE_LINE_ITEM}
                                </Button>

                                {!purchaseOrderData.isAuditRequired && purchaseOrderData.hasASN && (
                                    <Button variant="primary" tone={receivingMode.receivingCartonsActive ? 'critical' : 'plain'} onClick={receivingCartonButtonClick} loading={receivingCartonsLoading}>
                                        {receivingMode.receivingCartonsActive
                                            ? PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.STOP_RECEIVING_CARTONS
                                            : PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.RECEIVE_CARTON}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Header Information */}
                    <Layout.Section>
                        <PurchaseOrderReceivingHeader
                            poId={poId}
                            rdId={rdId}
                            asnId={asnId}
                            navigate={navigate}
                            passedState={passedState}
                            receivingLineItemsActive={receivingMode.receivingLineItemsActive}
                            receivingCartonsActive={receivingMode.receivingCartonsActive}
                            handleReceivingLineItems={onReceivingLineItemsClick}
                            handleReceivingCarton={onReceivingCartonClick}
                            status={documentStatus.status}
                            hasLocalStatusUpdate={documentStatus.hasLocalStatusUpdate}
                            purchaseOrder={purchaseOrderData.purchaseOrder}
                            currentStatus={purchaseOrderData.currentReceivingDocument?.status}
                            displayStatus={documentStatus.displayStatus}
                            parentData={passedState?.parentData}
                            name={name}
                            totalItems={totalItems}
                        />
                    </Layout.Section>

                    {/* Entry Section */}
                    {shouldShowEntrySection && (
                        <Layout.Section>
                            <PurchaseOrderEntrySection
                                passedState={passedState}
                                entryMode={receivingMode.entryMode}
                                setEntryMode={receivingMode.setEntryMode}
                                qtyValue={qtyValue}
                                setQtyValue={setQtyValue}
                                receivingLineItemsActive={receivingMode.receivingLineItemsActive}
                                onReceivingLineItemsClick={onReceivingLineItemsClick}
                                handleReceivingLineItems={onReceivingLineItemsClick}
                                receivingCartonsActive={receivingMode.receivingCartonsActive}
                                onReceivingCartonClick={onReceivingCartonClick}
                                handleReceivingCarton={onReceivingCartonClick}
                                setScannedVariants={variants.setScannedVariants}
                                setLastScanned={setLastScanned}
                                fetchProductFromServer={fetchProductFromServer}
                                poId={poId}
                                rdId={rdId}
                                receivingMode={receivingMode.receivingMode}
                                submitClicked={submitClicked}
                                purchaseOrder={purchaseOrderData.purchaseOrder}
                                displayStatus={documentStatus.displayStatus}
                                setLastAdded={setLastAdded}
                                lastAdded={lastAdded}
                                setHasInteracted={variants.setHasInteracted}
                                hasInteracted={variants.hasInteracted}
                            />
                        </Layout.Section>
                    )}

                    {/* Line Items Table */}
                    <Layout.Section>
                        <PurchaseOrderLineItemsTable
                            entryMode={receivingMode.entryMode}
                            mode={mode}
                            setMode={setMode}
                            getResourceId={getResourceId}
                            scannedVariants={variants.scannedVariants}
                            setScannedVariants={variants.setScannedVariants}
                            cartonRows={passedState?.cartonRows}
                            passedState={passedState}
                            purchaseOrder={purchaseOrderData.purchaseOrder}
                            submitClicked={submitClicked}
                            displayStatus={documentStatus.displayStatus}
                            setLastAdded={setLastAdded}
                            lastAdded={lastAdded}
                            productOnPurchaseOrder={productOnPurchaseOrder}
                            handleRemove={handleRemove}
                            setHasInteracted={variants.setHasInteracted}
                            setItemEditing={variants.setItemEditing}
                            setUnsavedEdits={variants.setUnsavedEdits}
                            hasUnsavedEdits={variants.hasUnsavedEdits}
                            showNavigationModal={navigationModal.showNavigationModal}
                            hasTriedToNavigateRows={navigationModal.hasTriedToNavigateRows}
                            resetTriedToNavigateForRow={navigationModal.resetTriedToNavigateForRow}
                            markRowTriedToNavigate={navigationModal.markRowTriedToNavigate}
                            onCancelEdit={(cancelFn) => {
                                cancelEditRef.current = cancelFn;
                            }}
                            onEditingIndexChange={(editingIndex) => {
                                currentEditingIndexRef.current = editingIndex;
                            }}
                        />
                    </Layout.Section>

                    {/* Action Buttons */}
                    {shouldShowActionButtons && (
                        <Layout.Section>
                            <PurchaseOrderActionButtons
                                passedState={passedState}
                                handleSubmitDocument={handleSubmitAndDismissBar}
                                handleSaveDraft={handleSaveDraftAndDismissBar}
                                hasLineItems={variants.scannedVariants.length > 0}
                                submitDocumentLoading={documentActions.submitDocumentLoading}
                                submitSaveDraftLoading={documentActions.submitSaveDraftLoading}
                                status={documentStatus.status}
                                hasLocalStatusUpdate={documentStatus.hasLocalStatusUpdate}
                                onLocalStatusUpdate={documentStatus.setHasLocalStatusUpdate}
                                entryMode={receivingMode.entryMode}
                                setEntryMode={receivingMode.setEntryMode}
                                purchaseOrder={purchaseOrderData.purchaseOrder}
                                displayStatus={documentStatus.displayStatus}
                                scannedVariants={variants.scannedVariants}
                                hasOverreceived={hasOverreceived}
                                productOnPurchaseOrder={productOnPurchaseOrder}
                                anyZeroReceived={anyZeroReceived}
                                saveDraftText={PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ACTION_BUTTONS.SAVE_DRAFT}
                                submitDocumentText={PURCHASE_ORDER_STRINGS.PURCHASE_ORDER_RECEIVING_DOCUMENT.PURCHASE_ORDER_ACTION_BUTTONS.SUBMIT_DOCUMENT}
                            />
                        </Layout.Section>
                    )}

                    <Layout.Section>
                        <Card>
                            <ActivityLog objectType="ReceivingDocument" objectId={rdId} />
                        </Card>
                    </Layout.Section>
                </Page>
            </Box>
        </>
    );
};

export default PurchaseOrderReceivingDocument;
